import { Link, Navigate } from "react-router-dom";
import {
  CUTOUT_UNIT_PRICE_KRW,
  MAX_IMAGES_PER_JOB,
  SERVICE_PERIOD,
  SERVICE_PRODUCT_NAME,
  VAT_INCLUDED,
  cutoutPrice,
  formatKrw,
} from "@makit/shared";

import { useSession } from "../../app/SessionGate";
import { PublicLayout } from "./PublicLayout";
import { LOGIN_PATH, PUBLIC_PATHS } from "./paths";

/** `P-01` 서비스 안내. 결제사 심사의 「결제 상품 · 서비스 확인 URL」.
 *
 * **글자는 목업 `P-01` 그대로다.** 위에서부터 무엇을(앱 소개 문구 · 서비스 설명) · 얼마에
 * (상품 이름 · 단가 · 한 건 최고 금액) · 어떻게 받는지(네 단계) · 누가 파는지(사업자 정보,
 * `App.tsx` 가 맨 아래에 붙인다) 순서로 읽힌다.
 *
 * **숫자를 이 파일에 적지 않는다.** 단가는 `CUTOUT_UNIT_PRICE_KRW`, 한 건 장수는
 * `MAX_IMAGES_PER_JOB`, 최고 금액은 그 둘의 곱(`cutoutPrice`)이다. 단가가 바뀌면 결제 화면과
 * 이 화면이 함께 바뀐다. 부가세 말은 단가 줄에만 둔다(PRD `7.0`).
 *
 * **문구의 정본은 PRD `7.0` 「로그인 전 화면의 확정 문구」 (2)다**(2026-10-04 `design-manager` 확정).
 *
 * ⛔ **약속하지 않는 것.** 결과가 나오는 시계는 결제가 아니라 **그 장의 만들기 시작**에 붙는다
 * (법무 `6A.14`(3) · `L-F53`). 결제한 뒤 셀러가 시작을 눌러야 만들기가 시작되므로 「결제 후 바로」라고
 * 적지 않는다. 돌아가는 금액은 조건만 말하고 때와 방법은 환불정책 제4조에 맡긴다(수동 취소 B안.
 * `6A.12` · `L-F50` · `L-F56`). 제공기간 값은 `SERVICE_PERIOD` 이고 결제사에 회신하는 제공기간과
 * 같은 값이어야 한다(`L-F53`).
 *
 * 예시 그림은 사진이 아니라 도형이다. **예시 사진은 우리가 직접 찍은 것만 쓴다**(PRD `2.1`)는
 * 규칙이 있고 그 사진이 아직 없다. 남의 상품 사진으로 채우지 않는다. */
export function ServiceIntroScreen() {
  const { session, loading } = useSession();

  // 로그인한 셀러가 `/` 로 오면 내 작업으로 보낸다. 이 주소는 그동안 내 작업으로 가는 입구였고
  // (옛 `*` → `/jobs`), 로그인한 셀러에게 `로그인` · `시작하기` 를 보이면 할 일이 없는 버튼이
  // 된다. 아직 모르는 동안은 아무것도 그리지 않는다. 그리면 로그인한 셀러에게 이 화면이 한 번
  // 스친다(`RequireAuth` 와 같은 이유).
  if (loading) return null;
  if (session.authenticated) return <Navigate to="/jobs" replace />;

  const unitPrice = formatKrw(CUTOUT_UNIT_PRICE_KRW);

  return (
    <PublicLayout title={`makit · ${SERVICE_PRODUCT_NAME}`}>
      <div className="pubhero">
        <div>
          <h1>판매에 필요한 것을 만들어드립니다.</h1>
          <p className="lead">상품을 준비한 당신이, 더 쉽게 판매할 수 있도록.</p>
          <p className="prose">
            직접 찍은 상품 사진을 올리면 배경을 지우고 상세페이지에 쓸 이미지로 만들어 드립니다. 화면에서 받는
            디지털 서비스라 배송하는 물건이 없습니다.
          </p>
          <div className="cta">
            <Link to={LOGIN_PATH} className="btn">
              시작하기
            </Link>
            <Link to={PUBLIC_PATHS.refund} className="histlink">
              환불정책 ›
            </Link>
          </div>
        </div>
        <div className="beforeafter">
          <figure>
            <div className="shot before" aria-hidden="true">
              <span className="obj-a" />
            </div>
            <figcaption>올린 사진 · 예시</figcaption>
          </figure>
          <figure>
            <div className="shot thumb" aria-hidden="true">
              <span className="obj-a" />
            </div>
            <figcaption>배경을 지운 결과 · 예시</figcaption>
          </figure>
        </div>
      </div>

      <div className="pubsplit">
        <div className="offer">
          <span className="oname">{SERVICE_PRODUCT_NAME}</span>
          <span className="oprice">
            <span className="amt mono">{unitPrice}</span>
            <span className="unit">사진 1장당 · {VAT_INCLUDED}</span>
          </span>
          <span className="setlink">
            <span>결제</span>
            <span className="v">작업 한 건마다</span>
          </span>
          <span className="setlink">
            <span>한 건에 담는 사진</span>
            <span className="v mono">최대 {MAX_IMAGES_PER_JOB}장</span>
          </span>
          <span className="setlink">
            <span>한 건 최고 금액</span>
            <span className="v mono">{formatKrw(cutoutPrice(MAX_IMAGES_PER_JOB))}</span>
          </span>
          <span className="setlink">
            <span>결과 이미지</span>
            <span className="v">한 장의 만들기가 시작되면 보통 수 분 안에</span>
          </span>
          <span className="setlink">
            <span>배송</span>
            <span className="v">없음 · 화면에서 받습니다</span>
          </span>
          <span className="setlink">
            <span>제공기간</span>
            <span className="v">{SERVICE_PERIOD}</span>
          </span>
        </div>
        <div>
          <h2 className="pubh">이렇게 받습니다</h2>
          <ol className="steps">
            <li>
              <b>사진 올리기</b>. 폰으로 찍거나 파일에서 골라 한 건에 최대 {MAX_IMAGES_PER_JOB}장
            </li>
            <li>
              <b>결제</b>. 올린 장수 × {unitPrice}. 만들기 시작 전에 알려 주시면 전액 취소합니다
            </li>
            <li>
              <b>배경 지우기</b>. 결제한 뒤 시작을 누르면 장마다 차례로 만듭니다. 한 장은 보통 수 분 안에
              나옵니다
            </li>
            <li>
              <b>받기</b>. 결과를 확인하고 고친 뒤 파일로 받습니다
            </li>
          </ol>
          <p className="pubnote prose">
            한 번에 다 만들지 못하면 일부 장이 대기로 남을 수 있습니다. 못 만든 장은 결과가 나오지 않습니다. 그
            장은 다시 만들어도 더 내실 금액이 없고, 다시 만들지 않으시면 그 장 금액이 돌아갑니다. 자세한 것은 <Link to={PUBLIC_PATHS.refund}>환불정책</Link>에 있습니다.
          </p>
        </div>
      </div>
    </PublicLayout>
  );
}
